import React, { createContext, useContext, useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signIn, signOut, fetchAuthSession, confirmSignIn } from 'aws-amplify/auth';
import { awsConfig } from '../aws-config';

// Configure Amplify
Amplify.configure(awsConfig);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already authenticated on mount
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);

      // Sign out any existing user first to avoid "already signed in" error
      try {
        await signOut();
      } catch (signOutErr) {
        // Ignore sign out errors - there might not be a user to sign out
      }

      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password
      });

      if (isSignedIn) {
        await checkUser();
        return { success: true };
      }

      // Handle other sign-in steps (e.g., MFA, new password required)
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          requiresNewPassword: true,
          message: 'Please set a new password'
        };
      }

      return {
        success: false,
        message: 'Additional steps required'
      };
    } catch (err) {
      setError(err.message);
      return {
        success: false,
        message: err.message
      };
    }
  };

  const completeNewPassword = async (newPassword, userAttributes = {}) => {
    try {
      setError(null);
      const { isSignedIn, nextStep } = await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: userAttributes
        }
      });

      if (isSignedIn) {
        await checkUser();
        return { success: true };
      }

      return {
        success: false,
        message: 'Additional steps required: ' + nextStep.signInStep
      };
    } catch (err) {
      setError(err.message);
      return {
        success: false,
        message: err.message
      };
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const getAuthToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (err) {
      console.error('Error getting auth token:', err);
      return null;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    completeNewPassword,
    logout,
    getAuthToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
